import type {
  ColumnMeta,
  FkEdge,
  IndexMeta,
  RoutineMeta,
  SchemaSnapshot,
  TableMeta,
} from '@/core/engines/intellisenseTypes';
import type {
  MysqlSchemaColumn,
  MysqlSchemaObject,
  MysqlSchemaSnapshot,
} from '@/features/connections/services/language-services/mysqlSchemaTypes';
import { getCachedMysqlSchema } from '@/features/connections/services/language-services/mysqlSchemaCache';
import { classifyJsonColumn } from '@/features/sql-resolver/json/classify/classifyJsonColumn';
import { projectRoutines } from './projectionHelpers';

function projectColumn(c: MysqlSchemaColumn): ColumnMeta {
  const classification = classifyJsonColumn(c.dataType, 'mysql');
  const base: ColumnMeta = {
    name: c.name,
    dataType: c.dataType,
    nullable: c.isNullable,
    isPrimaryKey: c.isPrimaryKey,
    ordinal: c.ordinalPosition,
  };
  return classification.kind === 'not-json'
    ? base
    : { ...base, jsonClassification: classification };
}

/**
 * Project the native index rows the schema cache already fetched
 * (`information_schema.STATISTICS`, one query per database) into the bus
 * `IndexMeta` shape. MySQL always names the primary-key index `PRIMARY` and
 * forbids that name for any other index, so `isPrimary` reads off the name —
 * `information_schema` exposes no dedicated flag.
 */
function projectIndexes(o: MysqlSchemaObject): IndexMeta[] {
  return o.indexes.map((i) => ({
    name: i.name,
    columns: i.columns,
    isUnique: i.isUnique,
    isPrimary: i.name.toUpperCase() === 'PRIMARY',
  }));
}

/**
 * Project ONE native relation entry into its bus-shaped `TableMeta`. Exported
 * for the tiered snapshot load (SLICES.md § S7 items 1 + 8): phase 2 merges
 * columns / indexes page by page and re-projects only the relations a page
 * touched, instead of re-running the whole snapshot projection per page.
 */
export function projectMysqlTableEntry(o: MysqlSchemaObject, database: string): TableMeta {
  return {
    name: o.name,
    database,
    kind: o.type === 'view' ? 'view' : 'table',
    columns: o.columns.map(projectColumn),
    indexes: projectIndexes(o),
  };
}

const projectTable = projectMysqlTableEntry;

/**
 * Group MySQL's per-row FK records (one row per (constraint, column)) into
 * `FkEdge`s by `constraintName`. Composite FKs surface as a single edge with
 * `fromColumns.length === toColumns.length > 1`. Single-column FKs are the
 * common case and produce a length-1 edge. The cache stores FK rows on the
 * source table only; `referencedTable` is the bare name (no schema).
 */
export function projectMysqlForeignKeys(table: MysqlSchemaObject): FkEdge[] {
  const byConstraint = new Map<
    string,
    { fromColumns: string[]; toTable: string; toColumns: string[] }
  >();
  for (const fk of table.foreignKeys) {
    const existing = byConstraint.get(fk.constraintName);
    if (existing) {
      existing.fromColumns.push(fk.columnName);
      existing.toColumns.push(fk.referencedColumn);
    } else {
      byConstraint.set(fk.constraintName, {
        fromColumns: [fk.columnName],
        toTable: fk.referencedTable,
        toColumns: [fk.referencedColumn],
      });
    }
  }
  return Array.from(byConstraint.entries()).map(([name, edge]) => ({
    fromTable: table.name,
    fromColumns: edge.fromColumns,
    toTable: edge.toTable,
    toColumns: edge.toColumns,
    name,
  }));
}

/**
 * Pure projection: native MySQL snapshot → bus `SchemaSnapshot`.
 */
export function projectMysqlSnapshot(native: MysqlSchemaSnapshot): SchemaSnapshot {
  const tables: TableMeta[] = [];
  for (const t of native.tables) tables.push(projectTable(t, native.database));
  for (const v of native.views) tables.push(projectTable(v, native.database));

  const routines: RoutineMeta[] = projectRoutines(
    native.routinesByNameLower,
    native.database,
  );

  const foreignKeys: FkEdge[] = [];
  for (const t of native.tables) foreignKeys.push(...projectMysqlForeignKeys(t));

  return {
    engineId: 'mysql',
    connectionId: native.connectionId,
    database: native.database,
    schemas: native.databases ?? [],
    tables,
    routines,
    foreignKeys,
    serverVersion: native.serverVersion || undefined,
    builtAt: native.builtAt,
  };
}

export function buildSnapshot(
  connectionId: string,
  database: string,
): SchemaSnapshot | null {
  const native = getCachedMysqlSchema(connectionId);
  if (!native || native.database !== database) return null;
  return projectMysqlSnapshot(native);
}
